/**
 * Draft-day tools, for the admin and the commissioners. Pass 16 rev B.
 *
 * Four boards that answer four different questions on draft night, and one rule running through
 * all of them: SAY WHAT YOU DO NOT KNOW. A tool that guesses is fine. A tool that guesses and
 * looks certain is how somebody drafts a running back in the ninth round because a screen implied
 * he would be gone.
 *
 * So the ADP board names its sources and shows where they disagree; the adjusted board shows how
 * far each player moved and why; the keeper projection marks a declared slate as a fact and a
 * modelled one as a model; and the next-four projection is labelled a guess, prints its reasoning
 * beside every pick, and carries its confidence as INK rather than as a caption (call 3).
 *
 * THE PAGE HAS A SEASON. The three boards built on a coming draft render from the offseason
 * through draft day; from the day after the draft the page is one calm card and the tendencies,
 * which never go stale. A loaded ADP file is ignored in-season, not deleted; it returns with the
 * next offseason's keeper math.
 */

import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import {
  adjusted, adpFile, adpVersion, clearAdp, consensus, disagreements, hasAdp, loadAdp, needs,
  onAdpChanged, project, sourceWeights, type ProjectedPick,
} from '../domain/draftTools.js';
import { tendencies } from '../domain/tendencies.js';
import { gmOfTeam, managerShort } from '../domain/identity.js';
import { Linkify } from './RuleText.js';
import { GMS } from '../store/session.js';
import {
  atLeast, type DraftState, type KeeperDeclaration, type LeagueSettings, type LeagueStore, type Role,
} from '../store/types.js';
import keeperRows from '../data/keepers-2026.json';
import { LEAGUE_2026 } from '../domain/config.js';
import { ownedRoundsFor } from '../domain/picks2026.js';
import { useOwnership } from './useOwnership.js';
import { projectKeepers, type Candidate, type ProjectedKeeper } from '../domain/projectedKeepers.js';
import { phaseOf } from '../domain/phase.js';
import { splitName } from '../domain/keeperRow.js';
import { GmBadge } from './GmBadge.js';
import { dock } from './dock.js';
import { useSheetSwipe } from './useSheetSwipe.js';

type Panel = 'next' | 'adp' | 'keepers' | 'tendencies';

const PANELS: Array<{ id: Panel; label: string }> = [
  { id: 'next', label: 'Next 4' },
  { id: 'adp', label: 'ADP' },
  { id: 'keepers', label: 'Keepers' },
  { id: 'tendencies', label: 'Tendencies' },
];

/** Confidence steps for the provenance dot (call 3): ink at 100 / 55 / 25%. */
export const FIRM_AT = 0.35;
export const LEAN_AT = 0.15;
export type Provenance = 'kept' | 'firm' | 'lean' | 'guess';
export function provenance(confidence: number | null): Provenance {
  if (confidence === null) return 'kept';
  if (confidence >= FIRM_AT) return 'firm';
  if (confidence >= LEAN_AT) return 'lean';
  return 'guess';
}

export function DraftTools({ role, store }: { role: Role; store: LeagueStore }) {
  const version = useSyncExternalStore(onAdpChanged, adpVersion, adpVersion);
  const [settings, setSettings] = useState<LeagueSettings | null>(null);
  useEffect(() => {
    let live = true;
    const load = () => void store.settings(SEASON).then((s) => { if (live) setSettings(s); });
    load();
    const off = store.subscribe(SEASON, (w) => { if (w === 'settings') load(); });
    return () => { live = false; off(); };
  }, [store]);
  const phase = phaseOf(settings);
  const dormant = phase === 'inseason';
  /* Next four by default on draft day, ADP otherwise; tendencies once the draft is done. */
  const [panel, setPanel] = useState<Panel | null>(null);
  const shown: Panel = panel ?? (dormant ? 'tendencies' : phase === 'draftday' ? 'next' : 'adp');

  // Belt and braces. The nav already hides this, and a screen that rewrites the draft board
  // should refuse to render rather than rely on somebody else's check.
  if (!atLeast(role, 'commissioner')) {
    return (
      <div className="app admin dtools">
        <section className="card actstate">
          <h3>These tools are the commissioners’</h3>
          <p className="fact">A tab you cannot use is not drawn; this address reached the page anyway.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="app admin dtools">
      <Linkify>
        {dormant ? (
          <section className="card actstate dormant">
            <div className="dormanthead">
              <h3>The {SEASON} draft is done</h3>
              <span className="pill grey">In-season</span>
            </div>
            <p className="fact">
              ADP unloads itself; the boards return when {SEASON + 1} keeper math exists. How they
              draft stays readable all year, the one board that never goes stale.
            </p>
          </section>
        ) : (
          <div className="dttabs" role="tablist" aria-label="Which board">
            {PANELS.map((p) => (
              <button key={p.id} type="button" role="tab" aria-selected={shown === p.id}
                className={`kindchip ${shown === p.id ? 'on' : ''}`} onClick={() => setPanel(p.id)}>
                {p.label}
              </button>
            ))}
          </div>
        )}

        {!dormant && shown === 'adp' && <Adp version={version} store={store} />}
        {(dormant || shown === 'tendencies') && <Habits />}
        {!dormant && shown === 'keepers' && <ProjectedKeepers version={version} store={store} />}
        {!dormant && shown === 'next' && <Next version={version} store={store} settings={settings} />}
      </Linkify>
    </div>
  );
}

/** `SMITH Jeremiah`, the house name line. A defence has no given name. */
function Name({ full, pos, weight }: { full: string; pos?: string | null; weight?: number }) {
  const { surname, given } = pos === 'DEF' ? { surname: full.replace(/\s+DEF$/i, ''), given: 'DEF' } : splitName(full);
  return (
    <span className="dtname" style={weight ? { fontWeight: weight } : undefined}>
      <b>{surname.toUpperCase()}</b>{given && <span className="given">{given}</span>}
      {pos && <span className={`poschip pos-${pos}`}>{pos}</span>}
    </span>
  );
}

const shortDate = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00Z`)
  .toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

/* -- ADP --------------------------------------------------------------------------------------- */

const SOURCE_SHORT: Record<string, string> = { sleeper: 'SLEEPER', espn: 'ESPN', ffc: 'FFC', fantasypros: 'FP' };
const shortSource = (id: string) => SOURCE_SHORT[id] ?? id.toUpperCase();
/** "Sleeper (real drafts)" → name "Sleeper", flavour "real drafts". */
function splitLabel(label: string): { name: string; flavour: string } {
  const m = /^(.*?)\s*\((.*)\)\s*$/.exec(label);
  return m ? { name: m[1]!, flavour: m[2]! } : { name: label, flavour: '' };
}

function Adp({ version, store }: { version: number; store: LeagueStore }) {
  const file = useMemo(adpFile, [version]);
  const rows = useMemo(() => consensus(), [version]);
  const { teams } = useKeeperProjection(store, version);
  const kept = useMemo(() => Object.values(keptByGm(teams)).flat(), [teams]);
  const board = useMemo(() => adjusted(kept), [version, kept]);
  const argued = useMemo(() => disagreements(), [version]);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const shownRows = showAll ? board : board.slice(0, 60);

  async function take(f: File) {
    setErr(null);
    try {
      const out = loadAdp(JSON.parse(await f.text()));
      setMsg(`${out.players} players from ${out.sources.join(', ') || 'no named source'}.`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'That file could not be read.');
    }
  }

  return (
    <>
      <section className="card">
        <div className="cardhead">
          <h3>Where these numbers come from</h3>
          <span className="dtacts">
            <label className="linkbtn filebtn">
              Load an ADP file
              <input type="file" accept="application/json,.json" hidden
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void take(f); e.target.value = ''; }} />
            </label>
            {hasAdp() && (
              <button type="button" className="linkbtn danger" onClick={() => { clearAdp(); setMsg(null); }}>
                Unload
              </button>
            )}
          </span>
        </div>
        {file.sources.length === 0 ? (
          <div className="dtempty">
            <b>No ADP loaded</b>
            <span>
              Nothing to project from. Bring a board in with <code>npm run data:adp</code> on a machine
              with normal internet, or load a JSON file here.
            </span>
          </div>
        ) : (
          <ul className="srccards">
            {file.sources.map((src) => {
              const { name, flavour } = splitLabel(src.label);
              return (
                <li key={src.id}>
                  <b>{name}</b>
                  <span>{src.players}{flavour ? ` · ${flavour}` : ''} · {shortDate(src.readOn)}</span>
                </li>
              );
            })}
          </ul>
        )}
        {file.sources.length > 0 && (
          <p className="cardfoot dtmono">
            {rows.length.toLocaleString()} in consensus · deployed, Yahoo’s own numbers arrive via /api/players
          </p>
        )}
        {msg && <p className="cardfoot fact said">{msg}</p>}
        {err && <p className="estaxbad pad">{err}</p>}
      </section>

      {rows.length > 0 && (
        <div className="twoup adpsplit">
          <section className="card">
            <div className="cardhead">
              <h3>Adjusted to our board</h3>
              <span className="mut">ten teams, {kept.length} keepers off the top — everybody moves up</span>
            </div>
            <table className="dttab">
              <thead>
                <tr>
                  <th>Player</th>
                  <th className="num">ADP</th><th className="num">Src</th>
                  <th className="num">Our pick</th><th className="num">Rd</th>
                  <th className="num">Drift</th>
                </tr>
              </thead>
              <tbody>
                {shownRows.map((r) => (
                  <tr key={`${r.name}|${r.pos}`}>
                    <th scope="row"><Name full={r.name} pos={r.pos} /></th>
                    <td className="num mut">{r.adp.toFixed(1)}</td>
                    <td className="num mut">{r.sources}</td>
                    <td className="num strong">{r.ourPick}</td>
                    <td className="num mut">Rd {r.ourRound}</td>
                    <td className={`num ${Math.round(r.drift) < 0 ? 'good' : Math.round(r.drift) > 0 ? 'warnnum' : 'mut'}`}>
                      {Math.round(r.drift) > 0 ? '+' : ''}{Math.round(r.drift) === 0 ? '0' : r.drift.toFixed(0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="poolfoot">
              {showAll
                ? <>All {board.length.toLocaleString()} · <button type="button" className="linkbtn" onClick={() => setShowAll(false)}>Back to 60</button></>
                : <>60 rows · <button type="button" className="linkbtn" onClick={() => setShowAll(true)}>Show all</button></>}
            </div>
          </section>

          {argued.length > 0 && (
            <section className="card">
              <div className="cardhead"><h3>Where the sources disagree</h3></div>
              <ol className="arglist">
                {argued.map((r) => (
                  <li key={r.name}>
                    <span className="who"><Name full={r.name} /></span>
                    <span className="spread">Δ {r.spread.toFixed(0)}</span>
                    <span className="detail">
                      {Object.entries(r.bySource).map(([src, n]) => `${shortSource(src)} ${n.toFixed(0)}`).join(' · ')}
                    </span>
                  </li>
                ))}
              </ol>
              <p className="cardfoot fact">The arguments worth having before draft night.</p>
            </section>
          )}
        </div>
      )}
    </>
  );
}

/* -- tendencies -------------------------------------------------------------------------------- */

function Habits() {
  const all = useMemo(() => tendencies(), []);
  const current = new Set(GMS.map((g) => String(g)));
  const rows = all.filter((t) => current.has(t.gm));

  return (
    <div className="habitcards">
      {rows.map((t) => (
        <section className="card" key={t.gm}>
          <div className="cardhead habithead">
            <GmBadge gm={t.gm} withName />
            <span className="dtmono">
              {t.drafts} drafts · {t.seasons[0]}–{t.seasons.at(-1)} · {t.resolved} of {t.picks} picks resolved
            </span>
          </div>
          <div className="habitbody">
            <span className="peyebrow">First four rounds</span>
            <div className="mixbar">
              {t.earlyMix.filter((m) => m.share > 0).map((m) => (
                <span key={m.pos} className={`mixseg pos-${m.pos}`} style={{ flexGrow: m.share }}
                  title={`${m.pos}: ${Math.round(m.share * 100)}% of his first four rounds (league ${Math.round(m.leagueShare * 100)}%)`}>
                  {m.share > 0.12 ? `${m.pos} ${Math.round(m.share * 100)}%` : m.share > 0.06 ? m.pos : ''}
                </span>
              ))}
            </div>
            {t.tendencies.length === 0
              ? <p className="habitline"><span className="habitlabel">—</span>Drafts like everybody else, nothing stands out.</p>
              : t.tendencies.map((x) => (
                <p className="habitline" key={x.label}>
                  <span className="habitlabel">{x.label}</span>{x.detail}
                </p>
              ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/* -- who is being kept, for every panel that needs to know ------------------------------------- */

const SEASON = 2026;

/**
 * THE ONE KEEPER PROJECTION, shared.
 *
 * It used to live inside the `Projected keepers` panel, and `Next four rounds` had this instead:
 *
 *     const KEPT: string[] = [];
 *
 * — a stub, under a comment describing the declarations file it was supposed to read. Everything
 * downstream took it at its word. The board was never cleared, so the projection drafted men who
 * are already kept: Amon-Ra St. Brown to Samant at 1.08 when he is Matt's franchise keeper, Josh
 * Allen to Tim at 1.09 when he is Amit's, Jayden Daniels to Tim at 4.09 when he is Ari's. Every
 * roster came back empty from `needs([])`, so all ten teams "needed" the same five positions and
 * the GM picker under the list changed nothing. Tim, 31 August: "the projected next four rounds
 * in my draft tools doesn't consider kept - and projected to be kept - players."
 *
 * Two panels now read the same numbers from here, so they cannot disagree about who is kept —
 * which was the other half of the bug waiting to happen.
 */
function useKeeperProjection(store: LeagueStore, version: number) {
  const [declarations, setDeclarations] = useState<KeeperDeclaration[]>([]);

  useEffect(() => {
    let live = true;
    const load = () => store.declarations(SEASON).then((d) => { if (live) setDeclarations(d); });
    void load();
    const off = store.subscribe(SEASON, (what) => {
      if (what === 'declarations') void load();
    });
    return () => { live = false; off(); };
  }, [store]);

  const candidates = useMemo<Candidate[]>(() => (keeperRows as unknown as Array<{
    player: string; team: string; pos: string; nflTeam: string | null;
    cost: number | null; actualCost?: number | null; mandatory?: boolean; farm?: boolean;
  }>).map((r) => ({
    player: r.player,
    gm: (gmOfTeam(r.team) ?? r.team).toUpperCase(),
    pos: r.pos,
    nflTeam: r.nflTeam,
    cost: r.actualCost ?? r.cost,
    mandatory: r.mandatory,
    farm: r.farm,
  })), []);

  const submitted = useMemo(() => {
    const out: Record<
      string, Array<{ player: string; franchiseTagged?: boolean; elevatedTo?: number }>
    > = {};
    for (const d of declarations) {
      if (d.status !== 'submitted') continue;
      out[d.gm.toUpperCase()] = d.keepers.map((k) => ({
        player: k.player, franchiseTagged: k.franchiseTagged, elevatedTo: k.elevatedTo,
      }));
    }
    return out;
  }, [declarations]);

  /**
   * WHO ACTUALLY OWNS WHICH PICK.
   *
   * This was `1..16` for every manager — a flat assumption that nobody had ever traded a pick, in
   * a league whose trade log runs to 109 rows. The keeper resolver places a man on a pick at or
   * before his cost round (2015-III-7), so a GM who had sold his 4th was still being projected
   * keeping somebody on it, and his slate came out one slot too generous. Same derivation the
   * draftboard uses, from the same ledger.
   */
  const { ownership } = useOwnership(store);
  const ownedRounds = useMemo(() => {
    const out: Record<string, number[]> = {};
    for (const g of GMS) {
      const gm = String(g).toUpperCase();
      out[gm] = ownedRoundsFor(gm, ownership, LEAGUE_2026.draftRounds);
    }
    return out;
  }, [version, ownership]);

  const teams = useMemo(
    () => projectKeepers({ candidates, ownedRounds, submitted, config: LEAGUE_2026 }),
    [candidates, ownedRounds, submitted, version],
  );

  return { teams, declarations, ownedRounds };
}

/** GM → the men he is keeping, declared or projected. Upper-cased, like every other GM key. */
function keptByGm(teams: readonly ReturnType<typeof projectKeepers>[number][]) {
  const out: Record<string, string[]> = {};
  for (const t of teams) out[t.gm.toUpperCase()] = t.keepers.map((k) => k.player);
  return out;
}

/* -- projected keepers -------------------------------------------------------------------------- */

/**
 * One priced row. Shared by the slate and by the not-kept list under it, so the two cannot drift
 * into being read against each other with different arithmetic in the columns. Rounds throughout
 * (rev B note 23): the Grid charges rounds and the order inside a round is unknown until the
 * lottery publishes.
 */
function PkRow({ k }: { k: ProjectedKeeper }) {
  const R = LEAGUE_2026.draftRounds;
  return (
    <tr className={`${k.basis === 'mandatory' ? 'forced' : ''}${k.basis === 'eligible' ? ' notkept' : ''}`}>
      <th scope="row" title={k.why || undefined}>
        {k.basis === 'mandatory' && <span className="ast" title={k.why}>◆ </span>}
        <Name full={k.player} pos={k.pos} />
        {k.franchiseTagged && <span className="pill amber">Franchise ×3</span>}
      </th>
      <td className="num">Rd {k.cost}</td>
      <td className="num faint" title={k.adpBeyondBoard ? 'No source ranks him inside the draft' : undefined}>
        {k.adpRound === null ? '—' : k.adpBeyondBoard ? `>R${R}` : `R${k.adpRound}`}
      </td>
      <td className="num strong"
        title={k.beyondBoard
          ? 'The board does not reach him — he would go undrafted here'
          : k.adpRound !== null && k.valueRound !== null && k.adpRound !== k.valueRound
            ? `ADP says R${k.adpRound}; he is R${k.valueRound} once the keepers come off`
            : undefined}>
        {k.valueRound === null ? '—' : k.beyondBoard ? `>R${R}` : `R${k.valueRound}`}
      </td>
      <td className={`num ${k.edge === null ? 'mut' : k.edge > 0 ? 'good' : k.edge < 0 ? 'warnnum' : 'mut'}`}>
        {k.edge === null ? '—' : k.edge > 0 ? `+${k.edge}` : k.edge}
      </td>
      <td className="num mut">{k.round ? `R${k.round}` : '—'}</td>
    </tr>
  );
}

function ProjectedKeepers({ version, store }: { version: number; store: LeagueStore }) {
  const [all, setAll] = useState(false);
  const { teams } = useKeeperProjection(store, version);
  const weights = useMemo(() => sourceWeights(), [version]);

  return (
    <>
      <div className="pktools">
        <span className="seg pkseg" role="tablist" aria-label="How much to show">
          <button type="button" role="tab" aria-selected={!all} className={!all ? 'on' : ''} onClick={() => setAll(false)}>Kept only</button>
          <button type="button" role="tab" aria-selected={all} className={all ? 'on' : ''} onClick={() => setAll(true)}>Every eligible</button>
        </span>
        <span className="dtmono weights">
          {weights.map((w) => `${splitLabel(w.label).name.replace('FF Calculator', 'FFC').replace('FantasyPros ECR', 'FP')} ×${w.weight}`).join(' · ')}
        </span>
      </div>

      <div className="pkgrid">
        {teams.map((t) => (
          <section className={`card pkteam${t.declared ? ' declared' : ''}`} key={t.gm}>
            <div className="cardhead">
              <GmBadge gm={t.gm} withName />
              <span className={`pill ${t.declared ? 'grey' : 'amber'}`}>{t.declared ? 'Declared' : 'Projected'}</span>
              <span className="dtmono pkhead">
                {t.declared ? '' : 'model: '}{t.keepers.length} keeper{t.keepers.length === 1 ? '' : 's'}
                {t.slotsUsed !== t.keepers.length ? ` · ${t.slotsUsed} slots` : ` · ${t.slotsUsed} slot${t.slotsUsed === 1 ? '' : 's'}`}
                {t.declared && (t.slotsUsed > LEAGUE_2026.freeKeepers ? ` · ${faabTax(t.slotsUsed)} tax` : ' · no tax')}
              </span>
            </div>
            {t.keepers.length === 0 && !all ? (
              <p className="fact pad">Nobody worth keeping on this roster.</p>
            ) : (
              <table className="dttab pktable">
                <thead>
                  <tr>
                    <th>Player</th><th className="num">Costs</th>
                    <th className="num" title="What the ADP board says, with nobody removed">ADP</th>
                    <th className="num" title="What he is worth once every projected and declared keeper comes off the board">Proj</th>
                    <th className="num" title="Costs − Proj. Positive is a bargain.">Edge</th>
                    <th className="num">Pick</th>
                  </tr>
                </thead>
                <tbody>
                  {t.keepers.map((k) => <PkRow key={k.player} k={k} />)}
                  {all && t.others.length > 0 && (
                    <tr className="pkrule">
                      <td colSpan={6}>not kept — {t.declared ? 'he passed on these' : 'the model passed on these'}</td>
                    </tr>
                  )}
                  {all && t.others.map((k) => <PkRow key={k.player} k={k} />)}
                </tbody>
              </table>
            )}
            {t.errors.length > 0 && (
              <ul className="dtlist bad">{t.errors.map((e) => <li key={e}>{e}</li>)}</ul>
            )}
          </section>
        ))}
      </div>
    </>
  );
}

/* -- next four rounds -------------------------------------------------------------------------- */

function Next({ version, store, settings }: { version: number; store: LeagueStore; settings: LeagueSettings | null }) {
  void settings;
  const { teams } = useKeeperProjection(store, version);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [gm, setGm] = useState<string>(String(GMS[0] ?? ''));
  const [openPick, setOpenPick] = useState<Slot | null>(null);

  useEffect(() => {
    let live = true;
    const load = () => store.draftState(SEASON).then((d) => { if (live) setDraft(d); });
    void load();
    const off = store.subscribe(SEASON, (what) => { if (what === 'draftState') void load(); });
    return () => { live = false; off(); };
  }, [store]);

  const rosters = useMemo(() => keptByGm(teams), [teams]);
  const kept = useMemo(() => Object.values(rosters).flat(), [rosters]);
  const declaredCount = teams.filter((t) => t.declared).length;

  /*
   * WHICH PICK EACH KEEPER IS STANDING ON, and therefore which picks a GM does not get to make.
   * 2015-III-7 charges a keeper against a round, so a manager keeping somebody at Round 2 does not
   * pick in Round 2. It is the projection's own answer (pins included), not a second one.
   */
  const keeperAt = useMemo(() => {
    const out: Record<string, Record<number, { player: string; pos?: string; cost: number }>> = {};
    for (const t of teams) {
      const gm = t.gm.toUpperCase();
      out[gm] = {};
      for (const k of t.keepers) {
        if (k.round === null) continue;
        out[gm][k.round] = { player: k.player, pos: k.pos, cost: k.cost };
      }
    }
    return out;
  }, [teams]);

  const board = useMemo(() => adjusted(kept), [version, kept]);
  const roster = useMemo(() => needs(rosters[gm.toUpperCase()] ?? []), [rosters, gm]);

  if (board.length === 0) {
    return (
      <section className="card">
        <div className="cardhead"><h3>Nothing to project from</h3></div>
        <p className="fact pad">
          The projection is built on the adjusted board, and no ADP has been loaded. Load one on
          the ADP board and this fills in. It will not guess without a board: a projection from
          no data is the app repeating its own assumptions back at you.
        </p>
      </section>
    );
  }

  /* The real choosing order: the published board, else the selection order, else the roster. */
  const order = (draft?.published && draft.order?.length === GMS.length ? draft.order
    : draft?.selectionOrder?.length === GMS.length ? draft.selectionOrder
      : GMS.map(String)).map((g) => String(g).toUpperCase());

  const slots: Slot[] = Array.from({ length: 4 * order.length }, (_, i) => {
    const round = Math.floor(i / order.length) + 1;
    const who = order[i % order.length]!;
    const code = `${round}.${String(i % order.length + 1).padStart(2, '0')}`;
    return { overall: i + 1, round, code, gm: who, keeper: keeperAt[who]?.[round] ?? null, pick: null };
  });
  const upcoming = slots.filter((s) => !s.keeper).map(({ overall, round, gm: who }) => ({ overall, round, gm: who }));
  const out = project(upcoming, rosters, kept, 4);
  const byOverall = new Map(out.picks.map((x) => [x.overall, x]));
  for (const s of slots) if (!s.keeper) s.pick = byOverall.get(s.overall) ?? null;
  const skipped = slots.length - upcoming.length;
  const needSays = roster.length
    ? `${roster.map((n) => n.pos).join(', ')} — kept ${(rosters[gm.toUpperCase()] ?? []).length || 'none'}`
    : 'nothing pressing';

  return (
    <>
      <section className="card nextcard">
        <div className="cardhead">
          <h3>Next four rounds</h3>
          <span className="pill amber">A guess</span>
          <span className="dtmono pkhead">{kept.length} keepers off · {declaredCount} of {GMS.length} slates declared</span>
        </div>
        <p className="fact pad nextnote">
          Greedy — everybody takes the best player on his own board. Habits come only from picks the bank recognises.
          {skipped > 0 && ` ${skipped} pick${skipped === 1 ? '' : 's'} in these rounds already belong to keepers and are shown in place.`}
        </p>
        <ol className="projrows">
          {slots.map((s) => {
            if (!s.keeper && !s.pick) return null;
            const prov: Provenance = s.keeper ? 'kept' : provenance(s.pick!.confidence);
            const weight = prov === 'kept' || prov === 'firm' ? 700 : prov === 'lean' ? 600 : 400;
            return (
              <li key={s.overall} className={`prov-${prov}`}>
                <button type="button" className="projopen" aria-label={`${s.code}, open the reasoning`} onClick={() => setOpenPick(s)} />
                <span className="rank">{s.code}</span>
                <span className={`provdot ${prov}`} aria-hidden="true" />
                <span className="who">{managerShort(s.gm)}</span>
                <span className="pl">
                  <Name full={s.keeper ? s.keeper.player : s.pick!.player} pos={s.keeper ? s.keeper.pos : s.pick!.pos} weight={weight} />
                  {s.keeper && <span className="pill green keeperpill">Keeper</span>}
                </span>
                <span className="why dtmono">{s.keeper ? `keeper · Rd ${s.keeper.cost} cost` : s.pick!.why}</span>
              </li>
            );
          })}
        </ol>
        <div className="nextfoot">
          <span className="legend">
            <span className="provdot kept" /> kept — certain
            <span className="legendgap" />
            <span className="provdot firm" /><span className="provdot lean" /><span className="provdot guess" /> model — ink is the confidence
          </span>
          <span className="spacer" />
          <label className="needsel">
            <span className="dtmono">Still needs:</span>
            <select value={gm} onChange={(e) => setGm(e.target.value)} aria-label="Whose roster">
              {GMS.map((g) => <option key={String(g)} value={String(g)}>{managerShort(String(g))}</option>)}
            </select>
          </label>
          <span className="dtmono needsays">{needSays}</span>
        </div>
      </section>
      {openPick && <WhySheet slot={openPick} onClose={() => setOpenPick(null)} />}
    </>
  );
}

interface Slot {
  overall: number; round: number; code: string; gm: string;
  keeper: { player: string; pos?: string; cost: number } | null;
  pick: ProjectedPick | null;
}

/** The why column, behind a row tap on a phone (rev B note 28). */
function WhySheet({ slot, onClose }: { slot: Slot; onClose: () => void }) {
  const swipe = useSheetSwipe(onClose);
  const prov: Provenance = slot.keeper ? 'kept' : provenance(slot.pick!.confidence);
  return dock(
    <>
      <button type="button" className="sheetscrim kdscrim" aria-label="Close" onClick={onClose} />
      <div className="kdsheet whysheet" role="dialog" aria-modal="true" aria-label={`Pick ${slot.code}`}
        style={swipe.style} {...swipe.handlers}>
        <span className="sheetgrip" aria-hidden="true" />
        <div className="whyhead">
          <span className="rank">{slot.code}</span>
          <span className={`provdot ${prov}`} aria-hidden="true" />
          <span className="who">{managerShort(slot.gm)}</span>
          <button type="button" className="photox" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <h2 className="kdsheeth">
          <Name full={slot.keeper ? slot.keeper.player : slot.pick!.player} pos={slot.keeper ? slot.keeper.pos : slot.pick!.pos} />
        </h2>
        <p className="fact">
          {slot.keeper
            ? `A keeper standing on this pick, charged at Round ${slot.keeper.cost}. The most certain thing on the board.`
            : slot.pick!.why}
        </p>
        {!slot.keeper && (
          <p className="dtmono">
            confidence {Math.round(slot.pick!.confidence * 100)}% · {prov === 'firm' ? 'likely' : prov === 'lean' ? 'leaning' : 'a guess'}
          </p>
        )}
      </div>
    </>,
  );
}

const faabTax = (slots: number): string =>
  `₣${LEAGUE_2026.luxuryTaxByKeeperCount[Math.min(slots, LEAGUE_2026.maxKeepers)] ?? 0}`;
